/**
 * Prediction Schemas (F05)
 *
 * Zod schemas for AI predictions, anomaly detection, and root cause analysis.
 * Uses discriminated union on prediction_type for type-safe prediction payloads.
 *
 * @module services/schemas/predictionSchemas
 */

import { z } from 'zod';

// ============================================
// HELPERS
// ============================================

/** Lenient UUID — accepts any UUID-formatted string (test UUIDs may not be RFC 4122 compliant) */
const dbUuid = z.string().regex(
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
);

// ============================================
// CONSTANTS
// ============================================

export const PREDICTION_TYPES = [
  'shrinkage_forecast',
  'exception_risk',
  'anomaly',
  'root_cause',
] as const;

export type PredictionType = (typeof PREDICTION_TYPES)[number];

export const PREDICTION_TYPE_LABELS: Record<PredictionType, string> = {
  shrinkage_forecast: 'Pronóstico de Merma',
  exception_risk: 'Riesgo de Excepción',
  anomaly: 'Anomalía Estadística',
  root_cause: 'Análisis de Causa Raíz',
};

export const SUBJECT_TYPES = ['cashier', 'store', 'category', 'product'] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

// ============================================
// PREDICTION PAYLOAD SCHEMAS (discriminated union)
// ============================================

export const ContributingFactorSchema = z.object({
  factor: z.string(),
  weight: z.number(),
});

export const ShrinkageForecastSchema = z.object({
  predicted_rate: z.number(),
  confidence: z.number(),
  window_days: z.number(),
  contributing_factors: z.array(ContributingFactorSchema),
});

export type ShrinkageForecast = z.infer<typeof ShrinkageForecastSchema>;

export const EvidenceItemSchema = z.object({
  metric: z.string(),
  value: z.number(),
  trend: z.enum(['up', 'down', 'stable']),
});

export const ExceptionRiskSchema = z.object({
  risk_score: z.number(),
  likely_type: z.string(),
  evidence: z.array(EvidenceItemSchema),
});

export type ExceptionRisk = z.infer<typeof ExceptionRiskSchema>;

export const AnomalyPayloadSchema = z.object({
  metric: z.string(),
  expected: z.number(),
  actual: z.number(),
  deviation_sigma: z.number(),
  direction: z.enum(['above', 'below']),
});

export type AnomalyPayload = z.infer<typeof AnomalyPayloadSchema>;

export const ProbableCauseSchema = z.object({
  cause: z.string(),
  confidence: z.number(),
});

export const RecommendedActionSchema = z.object({
  action: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
});

export const SupportingDataSchema = z.object({
  entity_type: z.string(),
  entity_id: z.string(),
});

export const RootCauseSchema = z.object({
  issue: z.string(),
  probable_causes: z.array(ProbableCauseSchema),
  recommended_actions: z.array(RecommendedActionSchema),
  supporting_data: z.array(SupportingDataSchema).optional(),
});

export type RootCause = z.infer<typeof RootCauseSchema>;

// ============================================
// DB ROW SCHEMA
// ============================================

export const AIPredictionDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  store_id: dbUuid.nullable(),
  prediction_type: z.enum(PREDICTION_TYPES),
  subject_type: z.string().nullable(),
  subject_id: dbUuid.nullable(),
  prediction: z.record(z.string(), z.unknown()),
  confidence: z.number().nullable(),
  prediction_window_start: z.string().nullable(),
  prediction_window_end: z.string().nullable(),
  was_accurate: z.boolean().nullable(),
  feedback_notes: z.string().nullable(),
  generated_at: z.string(),
});

export type AIPredictionRow = z.infer<typeof AIPredictionDbSchema>;

// ============================================
// INPUT SCHEMAS
// ============================================

export const PredictionInputSchema = z.object({
  tenant_id: dbUuid,
  store_id: dbUuid.optional(),
  prediction_type: z.enum(PREDICTION_TYPES),
  subject_type: z.string().optional(),
  subject_id: dbUuid.optional(),
  prediction: z.record(z.string(), z.unknown()),
  confidence: z.number().min(0).max(1).optional(),
  prediction_window_start: z.string().optional(),
  prediction_window_end: z.string().optional(),
});

export type PredictionInput = z.infer<typeof PredictionInputSchema>;

export const PredictionFeedbackSchema = z.object({
  was_accurate: z.boolean(),
  feedback_notes: z.string().optional(),
});

export type PredictionFeedback = z.infer<typeof PredictionFeedbackSchema>;

// ============================================
// FILTER SCHEMAS
// ============================================

export const PredictionFiltersSchema = z.object({
  storeId: dbUuid.optional(),
  type: z.enum(PREDICTION_TYPES).optional(),
  subjectType: z.string().optional(),
  hasNoFeedback: z.boolean().optional(),
  limit: z.number().int().positive().optional(),
});

export type PredictionFilters = z.infer<typeof PredictionFiltersSchema>;

// ============================================
// GEMINI RESPONSE SCHEMAS (for validating AI output)
// ============================================

export const GeminiShrinkageForecastResponseSchema = z.object({
  predictions: z.array(z.object({
    store_id: z.string().optional(),
    predicted_rate: z.number(),
    confidence: z.number(),
    window_days: z.number(),
    contributing_factors: z.array(ContributingFactorSchema),
  })),
});

export const GeminiExceptionRiskResponseSchema = z.object({
  risks: z.array(z.object({
    store_id: z.string().optional(),
    risk_score: z.number(),
    likely_type: z.string(),
    evidence: z.array(EvidenceItemSchema),
  })),
});

export const GeminiRootCauseResponseSchema = z.object({
  issue: z.string(),
  probable_causes: z.array(ProbableCauseSchema),
  recommended_actions: z.array(RecommendedActionSchema),
  supporting_data: z.array(SupportingDataSchema).optional(),
});

export type GeminiRootCauseResponse = z.infer<typeof GeminiRootCauseResponseSchema>;
